import { withAbortTimeout } from "./withAbortTimeout";

export const VERSION_STORAGE_KEY = "grounded_app_version";
export const CLEANUP_PENDING_KEY = "grounded_cleanup_pending";

/** Hard cap on how long we WAIT for cleanup. Never blocks mount either way. */
export const CLEANUP_TIMEOUT_MS = 2000;

export function isPreviewHost() {
  const host = window.location.hostname;
  return host.endsWith(".lovableproject.com") || host.endsWith(".lovable.app");
}

/**
 * Unregisters every service worker and deletes every cache entry.
 * Behavior on success is unchanged from the original inline implementation.
 * Throws on failure so the caller can record that cleanup is still owed.
 */
export async function clearCachesAndServiceWorkers() {
  if ("serviceWorker" in navigator) {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  }

  if ("caches" in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — non-fatal */
  }
}

function safeRemove(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage unavailable — non-fatal */
  }
}

/**
 * Runs cleanup in the background, bounded by a hard timeout.
 * - Success -> clears the pending flag.
 * - Timeout / throw -> sets the pending flag so the NEXT boot retries.
 * Never rejects; never blocks the caller.
 */
export function runCleanupInBackground(): Promise<void> {
  return withAbortTimeout(CLEANUP_TIMEOUT_MS, () => clearCachesAndServiceWorkers(), {
    label: "Cache/SW cleanup",
  })
    .then(() => {
      safeRemove(CLEANUP_PENDING_KEY);
    })
    .catch((e) => {
      // A service worker that survives the budget would otherwise linger
      // forever and keep serving stale bundles — mark cleanup as still owed.
      safeSet(CLEANUP_PENDING_KEY, "1");
      console.warn("[App] Cache/SW clear failed or timed out; will retry next load", e);
    });
}

export type BootstrapOptions = {
  appVersion: string;
  isDev: boolean;
  versionBanner?: string;
  mount: () => void;
};

/**
 * Startup sequence. Invariants:
 * 1. The version key is written BEFORE any cleanup is attempted, so a hanging
 *    cleanup can never make the branch self-repeating.
 * 2. `mount()` is called synchronously — nothing async precedes it.
 * 3. Cleanup is fire-and-forget with a hard timeout, and records whether it
 *    still owes work so later boots retry it.
 */
export function bootstrap({ appVersion, isDev, versionBanner, mount }: BootstrapOptions) {
  const storedVersion = safeGet(VERSION_STORAGE_KEY);
  const cleanupPending = safeGet(CLEANUP_PENDING_KEY) === "1";
  const disableServiceWorker = isDev || isPreviewHost();

  const needsCleanup = disableServiceWorker || storedVersion !== appVersion || cleanupPending;

  if (storedVersion !== appVersion) {
    if (!disableServiceWorker) {
      console.log("[App] New build detected, clearing caches…");
    }
    // Written up front: breaks the "hang forever, retry the same branch" loop.
    safeSet(VERSION_STORAGE_KEY, appVersion);
  }

  if (versionBanner) {
    console.log(`%c${versionBanner}`, "color: #3d2a1f; font-weight: bold; font-size: 14px;");
  }

  // React mounts unconditionally, before any cleanup work is awaited.
  mount();

  if (needsCleanup) {
    void runCleanupInBackground();
  }
}
